import { create } from "zustand"
import type { Lesson, MicrocourseProject } from "@/types/lesson"
import { normalizeDeck } from "@ai-tutor/shared"
import * as lessonApi from "@/api/lesson"

// ─── Types ──────────────────────────────────────────────────────

export interface LessonSettings {
  theme: string
  fontFamily: string
  transition: string
  fontScale: number
  customColors: { primary: string; accent: string; bg: string }
}

interface LessonState {
  // Current lesson being viewed
  lesson: Lesson | null
  loading: boolean
  error: string | null

  // Local (unsaved) settings — applied to preview in real-time
  localSettings: LessonSettings | null
  settingsDirty: boolean

  // TTS generation status
  ttsLoading: boolean

  // Actions
  loadLesson: (lessonId: string) => Promise<void>
  clearLesson: () => void

  // 本地更新 deck（用于白板编辑等元素级修改后同步 UI，不重新请求）
  setDeck: (deck: MicrocourseProject) => void

  // Settings
  updateLocalSettings: (partial: Partial<LessonSettings>) => void
  saveSettings: () => Promise<void>
  resetSettings: () => void

  // TTS
  generateTTS: () => Promise<void>
}

/** Extract settings from a MicrocourseProject */
function extractSettings(project: MicrocourseProject): LessonSettings {
  return {
    theme: project.theme,
    fontFamily: project.fontFamily,
    transition: project.transition,
    fontScale: project.fontScale,
    customColors: project.customColors,
  }
}

/** Merge local settings into a project (for preview) */
function applySettings(project: MicrocourseProject, settings: LessonSettings): MicrocourseProject {
  return {
    ...project,
    theme: settings.theme,
    fontFamily: settings.fontFamily,
    transition: settings.transition,
    fontScale: settings.fontScale,
    customColors: settings.customColors,
  }
}

/** Normalize a raw lesson from backend */
function normalizeLesson(raw: any): Lesson {
  return {
    ...raw,
    deck: raw.deck ? normalizeDeck(raw.deck, raw.id) : null,
  }
}

export const useLessonStore = create<LessonState>((set, get) => ({
  lesson: null,
  loading: false,
  error: null,
  localSettings: null,
  settingsDirty: false,
  ttsLoading: false,

  loadLesson: async (lessonId) => {
    set({ loading: true, error: null })
    try {
      const raw = await lessonApi.getLesson(lessonId)
      const lesson = normalizeLesson(raw)
      const project = lesson.deck as MicrocourseProject | null
      set({
        lesson,
        loading: false,
        localSettings: project ? extractSettings(project) : null,
        settingsDirty: false,
      })
    } catch (e) {
      const msg = e instanceof Error ? e.message : "加载失败"
      set({ error: msg, loading: false })
    }
  },

  clearLesson: () => {
    set({ lesson: null, error: null, localSettings: null, settingsDirty: false })
  },

  setDeck: (deck) => {
    const { lesson } = get()
    if (!lesson) return
    set({ lesson: { ...lesson, deck } })
  },

  updateLocalSettings: (partial) => {
    const { localSettings } = get()
    if (!localSettings) return
    set({
      localSettings: { ...localSettings, ...partial },
      settingsDirty: true,
    })
  },

  saveSettings: async () => {
    const { lesson, localSettings } = get()
    if (!lesson || !localSettings) return

    set({ loading: true, error: null })
    try {
      const { deck } = await lessonApi.updateSettings({
        lessonId: lesson.id,
        ...localSettings,
      })
      const updatedDeck = normalizeDeck(deck, lesson.id) as MicrocourseProject
      set({
        lesson: { ...lesson, deck: updatedDeck },
        localSettings: extractSettings(updatedDeck),
        settingsDirty: false,
        loading: false,
      })
    } catch (e) {
      const msg = e instanceof Error ? e.message : "保存失败"
      set({ error: msg, loading: false })
    }
  },

  resetSettings: () => {
    const { lesson } = get()
    if (!lesson?.deck) return
    const project = lesson.deck as MicrocourseProject
    set({
      localSettings: extractSettings(project),
      settingsDirty: false,
    })
  },

  generateTTS: async () => {
    const { lesson } = get()
    if (!lesson) return

    set({ ttsLoading: true, error: null })
    try {
      const { deck } = await lessonApi.generateTTS(lesson.id)
      const updatedDeck = normalizeDeck(deck, lesson.id) as MicrocourseProject
      set({
        lesson: { ...lesson, deck: updatedDeck },
        ttsLoading: false,
      })
    } catch (e) {
      const msg = e instanceof Error ? e.message : "TTS生成失败"
      set({ error: msg, ttsLoading: false })
    }
  },
}))

/**
 * Get the preview-ready deck with local settings applied.
 * Use this in the component to get the deck that reflects unsaved changes.
 */
export function getPreviewDeck(lesson: Lesson | null, localSettings: LessonSettings | null): MicrocourseProject | null {
  if (!lesson?.deck) return null
  const project = lesson.deck as MicrocourseProject
  if (!localSettings) return project
  return applySettings(project, localSettings)
}
